import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import api from '../services/api';
import { SERVICE_LANDING_CONTENT } from '../data/serviceLandingContent';
import { SITE_LOGO_URL, SITE_BRAND_NAME } from '../constants/siteBranding';
import './ServiceDetailPage.css';

const SITE_NAME = SITE_BRAND_NAME;

function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    if (SERVICE_LANDING_CONTENT[slug]) return;
    api.get(`/services/${encodeURIComponent(slug)}`)
      .then((res) => {
        setService(res.data);
        setError(null);
      })
      .catch((err) => {
        setService(null);
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (slug && SERVICE_LANDING_CONTENT[slug]) {
    return <Navigate to={`/service/${slug}`} replace />;
  }

  if (loading) {
    return (
      <div className="service-detail-page">
        <Header />
        <main className="service-detail-main">
          <div className="container">
            <p className="service-detail-loading">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-page">
        <Header />
        <main className="service-detail-main">
          <div className="container">
            <div className="service-detail-error">
              <h1>Service Not Found</h1>
              <p>The service you're looking for doesn't exist or has been removed.</p>
              <Link to="/services" className="service-detail-back">View All Services</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://www.khodalfacilitymanagementservice.com';
  const canonicalUrl = `${siteOrigin}/services/${service.slug || slug}`;

  const metaTitle = `${service.title} in Ahmedabad | ${SITE_NAME}`;
  const metaDescription = service.description
    ? `${service.description} ${SITE_NAME} — ${service.title} services in Ahmedabad, Gujarat. Facility management & manpower support. Call +91 9265472938.`
    : `${service.title} in Ahmedabad - Professional service by ${SITE_NAME}. Call +91 9265472938 for a quote.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.title} in Ahmedabad`,
    description: metaDescription,
    provider: {
      '@type': 'LocalBusiness',
      name: SITE_NAME,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Office No. 210, Ravija Plaza, Thaltej Shilaj Road',
        addressLocality: 'Ahmedabad',
        addressRegion: 'Gujarat',
        postalCode: '380059',
        addressCountry: 'IN'
      },
      telephone: '+919265472938',
      areaServed: 'Ahmedabad, Gujarat'
    },
    areaServed: {
      '@type': 'City',
      name: 'Ahmedabad',
      containedInPlace: { '@type': 'State', name: 'Gujarat' }
    }
  };

  jsonLd.image = service.image_url || SITE_LOGO_URL;

  const shareImage = service.image_url || SITE_LOGO_URL;

  return (
    <div className="service-detail-page">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${service.title} Ahmedabad, ${service.title} service in Ahmedabad, ${service.category} Ahmedabad, valet parking Ahmedabad, facility management Ahmedabad, house keeping Ahmedabad, cleaning services Ahmedabad`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:image:alt" content={service.image_url ? `${service.title} - ${SITE_NAME}` : `${SITE_NAME} logo`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="service-detail-main">
        <article className="service-detail-article">
          <header className="service-detail-hero">
            <div className="container">
              <nav aria-label="Breadcrumb" className="service-breadcrumb">
                <Link to="/services">Services</Link>
                <span className="breadcrumb-sep">/</span>
                <span>{service.title}</span>
              </nav>
              <h1 className="service-detail-h1">{service.title} in Ahmedabad</h1>
              {service.description && (
                <p className="service-detail-intro">{service.description}</p>
              )}
            </div>
          </header>

          <div className="service-detail-body container">
            {service.image_url && (
              <div className="service-detail-image-wrap">
                <img
                  src={service.image_url}
                  alt={`${service.title} - ${SITE_NAME}, Ahmedabad`}
                  width="800"
                  height="480"
                  loading="eager"
                />
              </div>
            )}

            {service.details ? (
              <div
                className="service-detail-content wysiwyg"
                dangerouslySetInnerHTML={{ __html: service.details }}
              />
            ) : (
              <div className="service-detail-content">
                <p>{service.description}</p>
                <p>
                  {SITE_NAME} provides professional {service.title} services in Ahmedabad and Gujarat. 
                  Contact us for a free quote: <a href="tel:+919265472938">+91 9265472938</a> or 
                  <Link to="/inquiry"> submit an inquiry</Link>.
                </p>
              </div>
            )}

            <div className="service-detail-cta">
              <h2>Book {service.title} in Ahmedabad</h2>
              <p>Get a free quote for our professional {service.title} services.</p>
              <div className="service-detail-cta-buttons">
                <a href="tel:+919265472938" className="service-cta-btn">Call +91 9265472938</a>
                <Link to="/inquiry" className="service-cta-btn secondary">Get Free Quote</Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default ServiceDetailPage;
